import { __assign, __rest } from "tslib";
import { createArticlesApi, DEFAULT_PAGE, DEFAULT_PAGE_SIZE, } from './';
import { ItemType } from '../domain';
import { createHttpTestKit } from '../http/test-kit';
import { createFakePostResponder, createFakeGetResponder, createFakePutResponder, createFakeDeleteResponder } from '../test-utils';
export var createArticlesApiTestKit = function () {
    var fakeHttp = createHttpTestKit();
    var api = createArticlesApi('', fakeHttp);
    var testKit = {
        whenAdminSearched: createFakePostResponder(fakeHttp, '/articles/search/admin'),
        whenSearched: createFakePostResponder(fakeHttp, '/articles/search'),
        whenHelpcenterSearched: createFakePostResponder(fakeHttp, '/helpcenter/articles/search'),
        whenGetArticle: function (dto) {
            var url = dto === '*' ? '/articles/' : "/articles/" + dto.id + "?locale=" + dto.locale;
            return createFakeGetResponder(fakeHttp, url);
        },
        whenGetArticleByUri: function (dto) {
            var url = dto === '*' ? dto : "/articles/uri/" + dto.uri + "?locale=" + dto.locale;
            return createFakeGetResponder(fakeHttp, url);
        },
        whenGetExtendedArticle: function (dto) {
            var url = "/articles/" + dto.id + "/extended?locale=" + dto.locale;
            return createFakeGetResponder(fakeHttp, url);
        },
        whenGetArticleTasks: function (dto) {
            if (dto === '*') {
                return createFakeGetResponder(fakeHttp, dto);
            }
            return createFakeGetResponder(fakeHttp, "/articles/" + dto.id + "/tasks?locale=" + dto.locale + "&page=" + (dto.page || DEFAULT_PAGE) + "&pageSize=" + (dto.pageSize || DEFAULT_PAGE_SIZE));
        },
        whenGetArticleTask: function (dto) {
            if (dto === '*') {
                return createFakeGetResponder(fakeHttp, dto);
            }
            var id = dto.id, taskId = dto.taskId, locale = dto.locale;
            return createFakeGetResponder(fakeHttp, "/articles/" + id + "/tasks/" + taskId + "?locale=" + locale);
        },
        whenAddArticle: function (dto, fail) {
            return createFakePostResponder(fakeHttp, "/articles", fail)(dto);
        },
        whenSaveArticle: function (dto) {
            return createFakePutResponder(fakeHttp, "/articles/" + (dto !== '*' && dto.id))(dto);
        },
        whenUnpublishArticle: function (dto, fail) {
            return createFakePostResponder(fakeHttp, "/articles/" + (dto !== '*' && dto.id) + "/unpublish", fail)(dto);
        },
        whenDeleteArticle: function (dto) {
            return createFakeDeleteResponder(fakeHttp, "/articles/" + (dto !== '*' && dto.id) + "?locale=" + (dto !== '*' ? dto.locale : 'en'));
        },
        whenSaveAndPublishArticle: function (dto, fail) {
            return createFakePostResponder(fakeHttp, "/articles/" + (dto !== '*' && dto.id) + "/publish", fail)(dto);
        },
        whenGetArticleListPresenceInfo: function (dto) {
            return createFakePostResponder(fakeHttp, "/articles/presence")(dto);
        },
        whenGetArticlePresenceInfo: function (dto) {
            return createFakeGetResponder(fakeHttp, "/articles/" + (dto !== '*' && dto.id) + "/presence?locale=" + (dto !== '*' ? dto.locale : 'en') + "&maxAge=5");
        },
        whenReportArticlePresence: function (dto) {
            var dtoWithItemType = dto !== '*' && __assign(__assign({}, dto), { itemType: ItemType.ARTICLE });
            return createFakePostResponder(fakeHttp, "/articles/" + (dto !== '*' && dto.id) + "/presence")(dtoWithItemType);
        },
        whenDeleteArticlePresence: function (dto) {
            if (dto === '*') {
                return createFakeDeleteResponder(fakeHttp, dto);
            }
            return createFakeDeleteResponder(fakeHttp, "/articles/" + dto.id + "/presence?locale=" + dto.locale + "&itemType=" + ItemType.ARTICLE);
        },
        whenGetArticleCounters: function (dto) {
            return createFakeGetResponder(fakeHttp, "/articles/" + (dto !== '*' && dto.id) + "/counters?locale=" + (dto !== '*' ? dto.locale : 'en'));
        },
        whenMoveArticleToCategory: function (dto) {
            return createFakePostResponder(fakeHttp, "/articles/" + (dto !== '*' && dto.id) + "/move")({
                categoryId: dto === '*' ? dto : dto.categoryId
            });
        },
        whenGetArticleActivityEvents: function (dto) {
            if (dto === '*') {
                return createFakeGetResponder(fakeHttp, "/history/item");
            }
            var locale = dto.locale, id = dto.id, _a = dto.page, page = _a === void 0 ? 1 : _a, _b = dto.pageSize, pageSize = _b === void 0 ? 200 : _b;
            // tslint:disable-next-line: max-line-length
            return createFakeGetResponder(fakeHttp, "/history/item?itemId=" + id + "&locale=" + locale + "&itemType=" + ItemType.ARTICLE + "&page=" + page + "&pageSize=" + pageSize);
        },
        whenGetRelatedArticles: function (dto) {
            return createFakeGetResponder(fakeHttp, "/articles/" + (dto !== '*' && dto.id) + "/relations/admin?locale=" + (dto !== '*' && dto.locale));
        },
        whenGetHelpcenterRelatedArticles: function (dto) {
            return createFakeGetResponder(fakeHttp, "/articles/" + (dto !== '*' && dto.id) + "/relations?locale=" + (dto !== '*' && dto.locale));
        },
        whenSortRelatedArticles: function (dto) {
            if (dto === '*') {
                return createFakePostResponder(fakeHttp, dto)(dto);
            }
            return createFakePostResponder(fakeHttp, "/article/" + dto.id + "/relations/sort")(dto);
        },
        whenAddRelatedArticle: function (dto) {
            return createFakePostResponder(fakeHttp, "/articles/" + (dto !== '*' && dto.id) + "/relations")({
                relatedId: dto === '*' ? '*' : dto.relatedId,
                doInAllLangs: dto === '*' ? false : dto.doInAllLangs,
                locale: dto === '*' ? 'en' : dto.locale
            });
        },
        whenRemoveRelatedArticle: function (dto) {
            if (dto === '*') {
                return createFakeDeleteResponder(fakeHttp, dto);
            }
            var doInAllLangs = dto.doInAllLangs ? "&doInAllLangs=" + dto.doInAllLangs : '';
            return createFakeDeleteResponder(fakeHttp, "/articles/" + dto.id + "/relations/" + dto.relatedId + "?locale=" + dto.locale + doInAllLangs);
        },
        whenUpdateLabels: function (dto) {
            if (dto === '*') {
                return createFakePutResponder(fakeHttp, dto)(dto);
            }
            else {
                return createFakePutResponder(fakeHttp, "/articles/" + dto.id + "/labels")({
                    locale: dto.locale,
                    addedLabelIds: dto.addedLabelIds,
                    removedLabelIds: dto.removedLabelIds
                });
            }
        },
        whenUpdateArticleType: function (dto) {
            if (dto === '*') {
                return createFakePostResponder(fakeHttp, dto)(dto);
            }
            return createFakePostResponder(fakeHttp, "/articles/" + dto.id + "/changeType")({
                type: dto.type
            });
        },
        whenUpdateArticleResolution: function (dto) {
            if (dto === '*') {
                return createFakePostResponder(fakeHttp, dto)(dto);
            }
            return createFakePostResponder(fakeHttp, "/articles/" + dto.id + "/setResolution")({
                resolution: dto.resolution,
                locales: dto.locales,
                notify: dto.notify,
            });
        },
        whenAddTask: function (dto) {
            if (dto === '*') {
                return createFakePostResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, rest = __rest(dto, ["id"]);
            return createFakePostResponder(fakeHttp, "/articles/" + id + "/tasks")(rest);
        },
        whenUpdateTask: function (dto) {
            if (dto === '*') {
                return createFakePutResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, taskId = dto.taskId, rest = __rest(dto, ["id", "taskId"]);
            return createFakePostResponder(fakeHttp, "/articles/" + id + "/tasks/" + taskId)(rest);
        },
        whenAddTaskComment: function (dto) {
            if (dto === '*') {
                return createFakePostResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, taskId = dto.taskId, rest = __rest(dto, ["id", "taskId"]);
            return createFakePostResponder(fakeHttp, "/articles/" + id + "/tasks/" + taskId + "/notes")(rest);
        },
        whenCompleteTask: function (dto) {
            if (dto === '*') {
                return createFakePostResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, taskId = dto.taskId;
            return createFakePostResponder(fakeHttp, "/article/" + id + "/tasks/" + taskId + "/complete")({});
        },
        whenDeleteTask: function (dto) {
            if (dto === '*') {
                return createFakeDeleteResponder(fakeHttp, dto);
            }
            var id = dto.id, taskId = dto.taskId, locale = dto.locale;
            return createFakeDeleteResponder(fakeHttp, "/articles/" + id + "/tasks/" + taskId + "?locale=" + locale);
        },
        whenEditTaskComment: function (dto) {
            if (dto === '*') {
                return createFakePutResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, taskId = dto.taskId, noteId = dto.noteId, rest = __rest(dto, ["id", "taskId", "noteId"]);
            return createFakePutResponder(fakeHttp, "/articles/" + id + "/tasks/" + taskId + "/notes/" + noteId)(rest);
        },
        whenRemoveTaskComment: function (dto) {
            if (dto === '*') {
                return createFakeDeleteResponder(fakeHttp, dto);
            }
            var id = dto.id, taskId = dto.taskId, noteId = dto.noteId, locale = dto.locale;
            return createFakeDeleteResponder(fakeHttp, "/articles/" + id + "/tasks/" + taskId + "/notes/" + noteId + "?locale=" + locale);
        },
        whenReopenTask: function (dto) {
            if (dto === '*') {
                return createFakePostResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, taskId = dto.taskId;
            return createFakePostResponder(fakeHttp, "/articles/" + id + "/tasks/" + taskId + "/open")({});
        },
        whenGetArticleNotes: function (dto) {
            if (dto === '*') {
                return createFakeGetResponder(fakeHttp, dto);
            }
            var id = dto.id, locale = dto.locale;
            return createFakeGetResponder(fakeHttp, "/articles/" + id + "/notes?locale=" + locale);
        },
        whenAddArticleNote: function (dto) {
            if (dto === '*') {
                return createFakePostResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, rest = __rest(dto, ["id"]);
            return createFakePostResponder(fakeHttp, "/articles/" + id + "/notes")(rest);
        },
        whenDeleteArticleNote: function (dto) {
            if (dto === '*') {
                return createFakeDeleteResponder(fakeHttp, dto);
            }
            var id = dto.id, noteId = dto.noteId, locale = dto.locale;
            return createFakeDeleteResponder(fakeHttp, "/articles/" + id + "/notes/" + noteId + "?locale=" + locale);
        },
        whenUpdateArticleNote: function (dto) {
            if (dto === '*') {
                return createFakePutResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, noteId = dto.noteId, locale = dto.locale, content = dto.content;
            return createFakePutResponder(fakeHttp, "/articles/" + id + "/notes/" + noteId)({ text: content, locale: locale });
        },
        whenUpdateArticleKeywords: function (dto) {
            if (dto === '*') {
                return createFakePutResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, locale = dto.locale, textValues = dto.textValues;
            return createFakePutResponder(fakeHttp, "/articles/" + id + "/phrases")({ textValues: textValues, locale: locale });
        },
        whenGetArticleVersions: function (dto) {
            if (dto === '*') {
                return createFakeGetResponder(fakeHttp, dto);
            }
            var id = dto.id, locale = dto.locale;
            return createFakeGetResponder(fakeHttp, "/articles/" + id + "/versions?locale=" + locale);
        },
        whenAddArticleComment: function (dto) {
            if (dto === '*') {
                return createFakePostResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, rest = __rest(dto, ["id"]);
            return createFakePostResponder(fakeHttp, "/articles/" + id + "/comments")(rest);
        },
        whenGetArticleComments: function (dto) {
            if (dto === '*') {
                return createFakeGetResponder(fakeHttp, dto);
            }
            var id = dto.id, locale = dto.locale;
            return createFakeGetResponder(fakeHttp, "/articles/" + id + "/comments?locale=" + locale);
        },
        whenSetArticleCommentResolved: function (dto) {
            if (dto === '*') {
                return createFakePutResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, commentId = dto.commentId, resolved = dto.resolved;
            return createFakePutResponder(fakeHttp, "/articles/" + id + "/comments/" + commentId + "/resolved")({ resolved: resolved });
        },
        whenSetArticleCommentContent: function (dto) {
            if (dto === '*') {
                return createFakePutResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, commentId = dto.commentId, content = dto.content;
            return createFakePutResponder(fakeHttp, "/articles/" + id + "/comments/" + commentId)({ content: content });
        },
        whenDeleteArticleComment: function (dto) {
            if (dto === '*') {
                return createFakeDeleteResponder(fakeHttp, dto);
            }
            var id = dto.id, commentId = dto.commentId;
            return createFakeDeleteResponder(fakeHttp, "/articles/" + id + "/comments/" + commentId);
        },
        whenAddArticleCommentReply: function (dto) {
            if (dto === '*') {
                return createFakePostResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, commentId = dto.commentId, content = dto.content, locale = dto.locale;
            return createFakePostResponder(fakeHttp, "/articles/" + id + "/comments/" + commentId)({ content: content, locale: locale });
        },
        whenGetArticleCommentReplies: function (dto) {
            if (dto === '*') {
                return createFakeGetResponder(fakeHttp, dto);
            }
            var id = dto.id, commentId = dto.commentId;
            return createFakeGetResponder(fakeHttp, "/articles/" + id + "/comments/" + commentId + "/replies");
        },
        whenSetArticleCommentReplyContent: function (dto) {
            if (dto === '*') {
                return createFakePutResponder(fakeHttp, dto)(dto);
            }
            var id = dto.id, commentId = dto.commentId, commentReplyId = dto.commentReplyId, content = dto.content;
            return createFakePutResponder(fakeHttp, "/articles/" + id + "/comments/" + commentId + "/replies/" + commentReplyId)({ content: content });
        },
        whenDeleteArticleCommentReply: function (dto) {
            if (dto === '*') {
                return createFakeDeleteResponder(fakeHttp, dto);
            }
            var id = dto.id, commentId = dto.commentId, commentReplyId = dto.commentReplyId;
            return createFakeDeleteResponder(fakeHttp, "/articles/" + id + "/comments/" + commentId + "/replies/" + commentReplyId);
        },
        whenSaveArticleComment: function (dto) { return createFakePutResponder(fakeHttp, "/articles/" + (dto !== '*' && dto.id) + "/comments")(dto); },
        whenSendFeedback: function (dto) {
            return createFakePostResponder(fakeHttp, "/articles/" + (dto !== "*" && dto.id) + "/helpful")(dto);
        },
        whenVoteOnIssueOrFeature: function (dto) {
            return createFakePostResponder(fakeHttp, "/articles/" + (dto !== "*" && dto.id) + "/vote")(dto);
        },
        whenUnvoteOnIssueOrFeature: function (dto) {
            return createFakeDeleteResponder(fakeHttp, "/articles/" + (dto !== "*" && dto.id) + "/vote?locale=" + (dto !== "*" && dto.locale));
        },
        // bulk
        whenDeleteArticles: function (dto) { return createFakePostResponder(fakeHttp, '/articles/delete')(dto); },
        whenUnpublishArticles: function (dto) { return createFakePostResponder(fakeHttp, '/articles/unpublish')(dto); },
        whenPublishArticles: function (dto) { return createFakePostResponder(fakeHttp, '/articles/publish')(dto); },
        whenMoveArticlesToCategory: function (dto) { return createFakePostResponder(fakeHttp, '/articles/move')(dto); },
        whenEditArticlesLabels: function (dto) { return createFakePutResponder(fakeHttp, '/articles/labels')(dto); },
        whenAddArticlesTranslations: function (dto) { return createFakePostResponder(fakeHttp, '/articles/translations')(dto); },
        whenAddArticlesTask: function (dto) { return createFakePostResponder(fakeHttp, '/articles/tasks')(dto); },
    };
    return __assign(__assign({}, api), testKit);
};
//# sourceMappingURL=testkit.js.map